/* ==========================================================================
   Cleaner -- calendar
   Today (a schedule), Week (how much of what you offered is sold), and
   Month (the editable grid). Confirmed jobs block themselves.
   ========================================================================== */

import { useState } from 'react';
import { Icon } from '../../components/Icon';
import {
  Avatar, Card, Divider, Empty, JobChips, Note, SectionHead, Seg, Switch,
} from '../../components/ui';
import { AVAILABILITY, BOOKINGS, RECURRING, REQUESTS } from '../../data/seed';
import { DOW } from '../../data/catalog';
import {
  areaLabel, cleaner, dayState, house, milesBetweenPlaces, milesChip, placeOf, scheduleFor,
  weekLoad, townLabel,
} from '../../lib/domain';
import type { Booking, IsoDate, Weekday } from '../../types';
import {
  TODAY, TODAY_ISO, dayLong, dur, endTime, fmtDate, fmtTime, isoDate, minsOf, money, mondayOf, nowSlot, parseDate, dowLetter, dowShort, monthName,
} from '../../lib/time';
import { t, tf } from '../../lib/i18n';
import {
  activeCleaner, commit, nav, openSheet, removeBlock, removeTimeOff, saveWeek, setWeekdayTime,
  state, tab, toggleHidden, toggleIn, toggleWeekday,
} from '../../store';

export function Calendar() {
  const c = activeCleaner();
  /* Cleaner-only screen. A home reaching it means a bad link, not a crash. */
  if (!c) return <div className="screen-pad formgrid"><Empty big={t('c_cleaneronly')} /></div>;

  return (
    <div className="screen-pad calpage">
      <Seg
        options={[
          { id: 'today', label: t('today') },
          { id: 'week', label: t('week') },
          { id: 'month', label: t('month') },
        ]}
        value={state.calMode}
        onChange={(v) => { state.calMode = v; state.selDays = []; commit(); }}
      />
      {state.calMode === 'today' && <Today cleanerId={c.id} />}
      {state.calMode === 'week' && <Week cleanerId={c.id} />}
      {state.calMode === 'month' && <Month cleanerId={c.id} />}
    </div>
  );
}

/* ------------------------------------------------------------------ today */

function Today({ cleanerId }: { cleanerId: string }) {
  const c = cleaner(cleanerId);
  const jobs = scheduleFor(cleanerId, TODAY_ISO);
  const st = dayState(cleanerId, TODAY_ISO);
  const openToday = REQUESTS.filter(
    (r) => r.status === 'open' && r.date === TODAY_ISO && c.areas.includes(house(r.houseId).area),
  );
  const earned = jobs.reduce((s, b) => s + b.rate * b.hours, 0);

  return (
    <>
      <Card className={jobs.length ? 'tint' : ''}>
        <div className="row between">
          <div className="col">
            <span className="sub tight">{fmtDate(TODAY_ISO, { long: true })}</span>
            <b style={{ fontSize: 19 }}>
              {jobs.length ? tf(jobs.length > 1 ? 'tx_jobs_n' : 'tx_jobs_1', { n: jobs.length }) : t('tx_nothing_booked')}
            </b>
          </div>
          <div className="col" style={{ textAlign: 'right' }}>
            <span className="sub tight">{t('earnings')}</span>
            <span className="rate" style={{ fontSize: 22 }}>{money(earned)}</span>
          </div>
        </div>
        {(st.status === 'free' || st.status === 'partial') && (
          <span className="sub tight">
            {t('tx_still_avail')}{' '}
            {st.windows
              .map((w) => ({ ...w, from: w.from < nowSlot().start ? nowSlot().start : w.from }))
              .filter((w) => w.from < w.to)
              .map((w) => fmtTime(w.from) + '–' + fmtTime(w.to)).join(', ') || t('tx_nothing_more_today')}
          </span>
        )}
      </Card>

      {jobs.length ? (
        <div className="tl">
          {jobs.map((b, i) => (
            <TimelineItem key={b.id} b={b} next={jobs[i + 1]} />
          ))}
        </div>
      ) : (
        <Empty
          big={t('c_nojobstoday')}
          sub={t('c_stillshowing')}
          action={t('t_findwork')}
          onAction={() => tab('c_jobs')}
        />
      )}

      {openToday.length > 0 && (
        <Card className="warm">
          <SectionHead title={t('h_opentoday')} />
          {openToday.map((r, i) => (
            <div key={r.id}>
              {i > 0 && <Divider />}
              <div
                className="row between tap"
                onClick={() => nav('c_job', { id: r.id })}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') nav('c_job', { id: r.id }); }}
              >
                <div className="col">
                  <b style={{ fontSize: 14 }}>{house(r.houseId).displayName}</b>
                  <span className="sub">
                    {fmtTime(r.start)} · {r.hours}h · {areaLabel(house(r.houseId).area)}
                  </span>
                </div>
                <span className="rate">{money(r.rate)}<small>{t('perhour')}</small></span>
              </div>
            </div>
          ))}
        </Card>
      )}
    </>
  );
}

function TimelineItem({ b, next }: { b: Booking; next?: Booking }) {
  const h = house(b.houseId);
  const gap = next ? minsOf(next.start) - (minsOf(b.start) + b.hours * 60) : 0;
  const miles = next ? milesBetweenPlaces(placeOf(h), placeOf(house(next.houseId))) : 0;

  return (
    <>
      <div className="tl-item">
        <div className="tl-time">{fmtTime(b.start)}</div>
        <div className="tl-body">
          <Card onClick={() => nav('c_booking', { id: b.id })}>
            <div className="row top">
              <Avatar p={h} size="sm" />
              <div className="col grow">
                <b style={{ fontSize: 14.5 }}>{h.displayName}</b>
                <span className="sub">
                  {[townLabel(h), b.hours + ' ' + t('hours')].filter(Boolean).join(' · ')} · {money(b.rate * b.hours)}
                </span>
              </div>
            </div>
            <div className="chips"><JobChips ids={b.jobIds} max={3} /></div>
          </Card>
        </div>
      </div>
      {next && (
        <div className="tl-gap">
          <div />
          <div className="g">
            <Icon name="car" /> {milesChip(miles)} · {tf('tx_until_next', { d: dur(gap) })}
          </div>
        </div>
      )}
    </>
  );
}

/* ------------------------------------------------------------------- week */

function Week({ cleanerId }: { cleanerId: string }) {
  const monday = mondayOf(TODAY_ISO);
  const wk = weekLoad(cleanerId, monday);
  const max = Math.max(8, ...wk.days.map((d) => d.open + d.booked));
  const earned = wk.days.reduce((s, d) => {
    const b = BOOKINGS.find(
      (x) => x.cleanerId === cleanerId && x.date === d.iso && x.status === 'confirmed',
    );
    return s + (b ? b.rate * b.hours : 0);
  }, 0);

  return (
    <>
      <Card>
        <div className="row between">
          <div className="col">
            <span className="sub tight">{tf('tx_week_of', { d: fmtDate(monday, { long: true }) })}</span>
            <b style={{ fontSize: 19 }}>{tf('tx_pct_booked', { n: wk.pct })}</b>
          </div>
          <div className="col" style={{ textAlign: 'right' }}>
            <span className="sub tight">{t('earnings')}</span>
            <span className="rate" style={{ fontSize: 22 }}>{money(earned)}</span>
          </div>
        </div>

        <div className="load">
          {wk.days.map((d) => (
            <div
              className={'load-col' + (d.iso === TODAY_ISO ? ' istoday' : '')}
              key={d.iso}
            >
              <span className="n">{d.booked ? d.booked + 'h' : ''}</span>
              <div
                className={'load-bar' + (d.state.status === 'off' ? ' offday' : '')}
                onClick={() => {
                  state.selDays = [d.iso];
                  state.calMode = 'month';
                  openSheet({ name: 'dayedit', mode: 'available' });
                }}
                role="button"
                tabIndex={0}
                onKeyDown={() => {}}
              >
                {d.booked > 0 && <i className="bk" style={{ height: (d.booked / max) * 100 + '%' }} />}
                {d.open > 0 && <i className="op" style={{ height: (d.open / max) * 100 + '%' }} />}
              </div>
              <span className="d">{dowLetter(parseDate(d.iso).getDay())}</span>
            </div>
          ))}
        </div>

        <div className="cal-legend">
          <span><b style={{ background: 'var(--tile)' }} />{t('booked')} {wk.booked}h</span>
          <span><b style={{ background: 'var(--good-tint)' }} />{tf('tx_still_open', { n: wk.open })}</span>
        </div>

        {wk.open > 12 && (
          <Note warm>
            <b>{tf('tx_unsold', { n: wk.open })}</b> {t('tx_unsold_more')}
          </Note>
        )}
      </Card>

      {wk.days
        .filter((d) => d.state.status === 'booked')
        .map((d) => {
          const b = BOOKINGS.find(
            (x) => x.cleanerId === cleanerId && x.date === d.iso && x.status === 'confirmed',
          )!;
          const h = house(b.houseId);
          return (
            <Card key={b.id} onClick={() => nav('c_booking', { id: b.id })}>
              <div className="row top">
                <Avatar p={h} size="sm" />
                <div className="col grow">
                  <b style={{ fontSize: 14.5 }}>{h.displayName}</b>
                  <span className="sub">
                    {fmtDate(d.iso, { long: true })} · {fmtTime(b.start)}–{endTime(b.start, b.hours)}
                  </span>
                </div>
                <span className="rate">{money(b.rate * b.hours)}</span>
              </div>
            </Card>
          );
        })}
    </>
  );
}

/* ------------------------------------------------------------------ month */

function Month({ cleanerId }: { cleanerId: string }) {
  const av = AVAILABILITY[cleanerId];

  /* Which month is first on screen, counted from this one.

     These two grids used to be written as year={2026} month={7} and month={8}
     -- August and September, from when the prototype was built -- so in
     October a cleaner opened her calendar to two months that were already over
     and had no way to reach the one she was in. It now starts from today, from
     the server's clock, and moves either way a month at a time. */
  const [offset, setOffset] = useState(0);
  const firstOf = (n: number) => new Date(TODAY.getFullYear(), TODAY.getMonth() + offset + n, 1);
  const a = firstOf(0);
  const b = firstOf(1);
  const blockedDates = Object.keys(av.overrides).filter(
    (k) => (av.overrides[k].blocks || []).length > 0,
  ).sort();

  return (
    <>
      <Note warm>
        <b>{t('c_tapdays')}</b> {t('tx_tapdays_more')}
      </Note>

      <div className="row between monthnav">
        <button
          type="button"
          className="btn quiet sm"
          onClick={() => setOffset((o) => o - 1)}
          aria-label={t('tx_earlier_months')}
        >
          <Icon name="back" /> {monthName(firstOf(-1).getMonth())}
        </button>
        {offset !== 0 && (
          <button type="button" className="btn quiet sm" onClick={() => setOffset(0)}>
            {t('tx_this_month')}
          </button>
        )}
        <button
          type="button"
          className="btn quiet sm"
          onClick={() => setOffset((o) => o + 1)}
          aria-label={t('tx_later_months')}
        >
          {monthName(firstOf(2).getMonth())} <Icon name="fwd" />
        </button>
      </div>

      <MonthGrid cleanerId={cleanerId} year={a.getFullYear()} month={a.getMonth()} />
      <MonthGrid cleanerId={cleanerId} year={b.getFullYear()} month={b.getMonth()} />

      <Card>
        <SectionHead
          title={t('timeoff')}
          action={t('b_add')}
          onAction={() => openSheet({ name: 'timeoff' })}
        />
        {av.offPeriods.length ? (
          av.offPeriods.map((b, i) => (
            <div key={i}>
              {i > 0 && <Divider />}
              <div className="row between">
                <div className="col">
                  <b style={{ fontSize: 14 }}>
                    {fmtDate(b.from, { long: true })} → {fmtDate(b.to, { long: true })}
                  </b>
                  <span className="sub">{b.reason}</span>
                </div>
                <button type="button" className="btn quiet sm" onClick={() => removeTimeOff(i)}>
                  {t('b_remove')}
                </button>
              </div>
            </div>
          ))
        ) : (
          <span className="sub">{t('c_notimeoff')}</span>
        )}
      </Card>

      <Card>
        <SectionHead title={t('h_blocked')} />
        {blockedDates.length ? (
          blockedDates.map((iso) =>
            av.overrides[iso].blocks!.map((bl, bi) => (
              <div className="row between" key={iso + bi}>
                <div className="col">
                  <b style={{ fontSize: 14 }}>{fmtDate(iso, { long: true })}</b>
                  <span className="sub">
                    {fmtTime(bl.from)}–{fmtTime(bl.to)} · {bl.reason || t('tx_blocked')}
                  </span>
                </div>
                <button
                  type="button"
                  className="btn quiet sm"
                  onClick={() => removeBlock(iso, bi)}
                >
                  {t('b_remove')}
                </button>
              </div>
            )),
          )
        ) : (
          <span className="sub">
            {t('c_nothingblocked')}
          </span>
        )}
      </Card>

      <Card>
        <SectionHead
          title={t('usualweek')}
          action={t('b_edit')}
          onAction={() => nav('c_editweek')}
        />
        {DOW.map((d) => {
          const slots = av.weekly[d as Weekday] || [];
          return (
            <div className="row between" key={d}>
              <b style={{ fontSize: 14, width: 44 }}>{dowShort(DOW.indexOf(d as Weekday))}</b>
              <span className="sub grow">
                {slots.length
                  ? slots
                      .map((s) => fmtTime(s.split('-')[0]) + '–' + fmtTime(s.split('-')[1]))
                      .join(', ')
                  : t('tx_not_working')}
              </span>
              <span className={'chip' + (slots.length ? ' good' : '')}>
                {slots.length ? t('available') : t('tx_off')}
              </span>
            </div>
          );
        })}
      </Card>

      <Card>
        <SectionHead title={t('h_standing')} />
        {RECURRING.filter((x) => x.cleanerId === cleanerId).length ? (
          RECURRING.filter((x) => x.cleanerId === cleanerId).map((x) => (
            <div className="row top" key={x.id}>
              <Avatar p={house(x.houseId)} />
              <div className="col grow">
                <b>{house(x.houseId).displayName}</b>
                <span className="sub">
                  {tf('tx_every_other', { day: dayLong(x.day) })}, {fmtTime(x.start)} · {x.hours}h ·{' '}
                  {money(x.rate)}{t('perhour')}
                </span>
                <span className="sub tight">{tf('tx_since', { d: fmtDate(x.since, { long: true }) })}</span>
              </div>
            </div>
          ))
        ) : (
          <span className="sub">{t('c_nostanding')}</span>
        )}
      </Card>

      <Card className={av.hidden ? 'warm' : ''}>
        <div className="switchrow">
          <div className="col">
            <b style={{ fontSize: 14 }}>{t('c_showme')}</b>
            <span className="hint">
              {av.hidden
                ? t('tx_hidden')
                : t('tx_findable_cal')}
            </span>
          </div>
          <Switch on={!av.hidden} onClick={toggleHidden} />
        </div>
      </Card>
    </>
  );
}

function MonthGrid({
  cleanerId, year, month,
}: { cleanerId: string; year: number; month: number }) {
  const first = new Date(year, month, 1);
  const start = new Date(year, month, 1 - first.getDay());
  const cells = [];

  for (let i = 0; i < 42; i++) {
    const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    const iso: IsoDate = isoDate(d);
    const out = d.getMonth() !== month;
    /* Shown, so a cleaner can see what she worked, but not selectable:
       marking yesterday available changes nothing anybody can book. */
    const past = !out && iso < TODAY_ISO;
    const st = out ? null : dayState(cleanerId, iso);

    const cls = ['cal-day'];
    if (out) cls.push('out');
    else if (past) cls.push('past');
    else if (st!.status === 'booked') cls.push('booked');
    else if (st!.status === 'off') cls.push('blocked');
    else if (st!.status === 'partial') cls.push('partial');
    else cls.push('free');
    if (iso === TODAY_ISO) cls.push('today');
    if (state.selDays.includes(iso)) cls.push('sel');

    cells.push(
      <button
        key={iso + i}
        type="button"
        className={cls.join(' ')}
        disabled={out || past}
        onClick={() => { toggleIn(state.selDays, iso); commit(); }}
      >
        {d.getDate()}
      </button>,
    );
  }

  return (
    <div className="cal">
      <div className="cal-head">
        <b>{monthName(month)} {year}</b>
        <span className="sub tight">
          {tf('tx_cal_confirmed', { d: fmtDate(AVAILABILITY[cleanerId].lastConfirmed) })}
        </span>
      </div>
      <div className="cal-grid">
        {DOW.map((d, i) => <div className="cal-dow" key={d}>{dowLetter(i)}</div>)}
        {cells}
      </div>
      <div className="cal-legend">
        <span><b style={{ background: 'var(--good-tint)' }} />{t('available')}</span>
        <span><b style={{ background: 'var(--tile)' }} />{t('booked')}</span>
        <span><b style={{ background: 'var(--bad-tint)' }} />{t('unavailable')}</span>
        <span>
          <b style={{ background: 'linear-gradient(135deg,var(--good-tint) 0 50%,var(--warn-tint) 50%)' }} />
          {t('partly')}
        </span>
      </div>
    </div>
  );
}

/* --------------------------------------------------------- the usual week */

export function EditWeek() {
  const av = AVAILABILITY[state.cleanerId];
  return (
    <div className="screen-pad">
      <Note>
        {t('tx_default_week')}
      </Note>
      <Card>
        {DOW.map((d) => {
          const slots = av.weekly[d as Weekday] || [];
          const on = slots.length > 0;
          const [from, to] = on ? slots[0].split('-') : ['09:00', '17:00'];
          return (
            <div className="editrow" key={d}>
              <b>{dowShort(DOW.indexOf(d as Weekday))}</b>
              {on ? (
                <div className="times">
                  <input
                    type="time"
                    value={from}
                    onChange={(e) => setWeekdayTime(d as Weekday, 'from', e.target.value)}
                  />
                  <span className="sub">→</span>
                  <input
                    type="time"
                    value={to}
                    onChange={(e) => setWeekdayTime(d as Weekday, 'to', e.target.value)}
                  />
                </div>
              ) : (
                <span className="sub">{t('c_notworking')}</span>
              )}
              <Switch on={on} onClick={() => toggleWeekday(d as Weekday)} />
            </div>
          );
        })}
      </Card>
      <button type="button" className="btn block lg" onClick={saveWeek}>{t('b_save')}</button>
    </div>
  );
}
